// src/pages/AvatarSettings.js
import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useCallback
} from "react";
import axios from "axios";
import { useParams} from 'react-router-dom';

import backendURL from "../config";
import {
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination
} from "@mui/material";

const AvatarSettings = forwardRef(
  ({ isSetupMode = false, chatbotId, onComplete, onError }, ref
) => {
  /* -------------------------------------------------------------
   * 1.  State for avatar list and filters
   * -------------------------------------------------------------*/
  const [avatars, setAvatars] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 12,
    total_pages: 1,
    total_items: 0
  });
  const [filters, setFilters] = useState({
    search: "",
    type: "",
    category: ""
  });
  const [filterOptions, setFilterOptions] = useState({
    types: [],
    categories: []
  });
  const [loading, setLoading] = useState(true);
  const { chatbotId: paramsChatbotId } = useParams();
  let chatBotId = chatbotId ? chatbotId : paramsChatbotId


  /* ---------------------------- ---------------------------------
   * 2.  State for selection and saving
   * -------------------------------------------------------------*/
  const [selectedAvatar, setSelectedAvatar] = useState(null);      // pending pick
  const [savedAvatar, setSavedAvatar] = useState(null);            // confirmed
  const [errorDiv, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const token = localStorage.getItem("access_token");

  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  /* -------------------------------------------------------------
   * 3.  Fetch the user's SAVED avatar (only on mount & after save)
   * -------------------------------------------------------------*/
  const fetchUserAvatar = useCallback(() => {
    if (!token) return;


    const params = chatBotId ? { chatbot_id: chatBotId } : {};

    axios
      .get(`${backendURL}/get_avatar_configurations`, { headers, params })
      .then((response) => {
        const responseData =
          typeof response.data.data === "string"
            ? JSON.parse(response.data.data)
            : response.data.data;
        if (Array.isArray(responseData) && responseData.length > 0) {
          const savedAvatarName = responseData[0].avatar_name;
          const savedAvatarImgName = `${savedAvatarName}.png`;
          const finalAvatarData = {
            id: "saved",
            avatar_img_name: savedAvatarImgName,
            avatar_name: savedAvatarName
          }
          setSavedAvatar(finalAvatarData);
          setSelectedAvatar(finalAvatarData);
        } else {
          setSavedAvatar(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching user avatar configuration:", error);
      });
  }, [headers, token]);

  /* -------------------------------------------------------------
   * 4.  Fetch available avatars (filters / pagination)
   * -------------------------------------------------------------*/
  const fetchAvailableAvatars = useCallback(() => {
    if (!token) {
      const errorMsg = "Authentication token missing. Cannot load avatars.";
      if (isSetupMode) onError(errorMsg);
      else setError(errorMsg);
      setLoading(false);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({
      page: pagination.page,
      per_page: pagination.per_page,
      search: filters.search,
      type: filters.type,
      category: filters.category
    });

    axios
      .get(`${backendURL}/get_all_avatars?${params.toString()}`, { headers })
      .then((response) => {
        const data = response.data.data;
        setAvatars(data.avatars);
        setPagination((prev) => ({
          ...prev,
          total_pages: data.pagination.total_pages,
          total_items: data.pagination.total_items
        }));
        if (
          pagination.page === 1 &&
          !filters.search &&
          !filters.type &&
          !filters.category
        ) {
          setFilterOptions(data.filter_options);
        }
      })
      .catch((error) => {
        console.error("Error fetching available avatars:", error);
        const errorMsg =
          error.response?.data?.message || "Error fetching avatar list.";
        if (isSetupMode) onError(errorMsg);
        else setError(errorMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    token,
    pagination.page,
    pagination.per_page,
    filters,
    headers,
    isSetupMode,
    onError
  ]);

  /* -------------------------------------------------------------
   * 5.  Initial load
   * -------------------------------------------------------------*/
  useEffect(() => {
    if (!isSetupMode) {
      document.title = "Avatar Setting | KlonIT Web Application";
    }
    fetchUserAvatar();
  }, [isSetupMode, fetchUserAvatar]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAvailableAvatars();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchAvailableAvatars]);

  /* -------------------------------------------------------------
   * 6.  Handlers
   * -------------------------------------------------------------*/
  const handleAvatarSelection = (avatar) => {
    setSelectedAvatar(avatar);
    setError(null);
  };

  const handleAvatarCardClassName = (avatar) => {
    let classNames = "card avatar_selection";
    if (selectedAvatar?.avatar_img_name === avatar.avatar_img_name) {
      classNames += " selected";
    }
    return classNames;
  };

  const triggerSave = async () => {

    if (!selectedAvatar || !selectedAvatar.avatar_img_name) {
      console.log(savedAvatar)
      if (savedAvatar) {
        setSelectedAvatar(savedAvatar);
      }else{
        const noSelectionMsg = "No avatar selected. Please choose an avatar.";
        if (isSetupMode) onError(noSelectionMsg);
        else setError(noSelectionMsg);
        return false;
      }
     
    }
    if (!token) {
      const noTokenMsg = "Authentication token missing. Cannot save avatar.";
      if (isSetupMode) onError(noTokenMsg);
      else setError(noTokenMsg);
      return false;
    }

    setIsSaving(true);
    setError(null);

    const imagePath = `images/avatars/${selectedAvatar.avatar_img_name}`;
    const dataToSave = { avatar_name: imagePath , chatbot_id: chatBotId};

    try {
      const response = await axios.post(
        `${backendURL}/update_avatar_configuration`,
        dataToSave,
        { headers }
      );

      // update localStorage
      try {
        let userData = JSON.parse(localStorage.getItem("user") || "{}");
        userData.avatar_settings = { avatar_name: imagePath };
        userData.chatbot_setup_configuration = {
          ...userData.chatbot_setup_configuration,
          is_avatar_set: true
        };
        localStorage.setItem("user", JSON.stringify(userData));
      } catch (e) {
        console.error("Failed to update localStorage with avatar:", e);
        if (isSetupMode) {
          onError("Avatar saved, but failed to update local session data.");
        }
      }

      // refresh saved avatar & avatars list
      fetchUserAvatar();
      fetchAvailableAvatars();

      if (isSetupMode) {
        if (onComplete) onComplete({ nextSlug: 'set-audio-language'  }); // Or get next slug dynamically
        // setIsSaving(false); // Moved to finally block
        return true;
      } else {
        setError(response.data.message || "Avatar saved successfully!");
        setTimeout(() => setError(null), 2000);
      }
      return true;
    } catch (error) {
      console.error("Error saving avatar configuration:", error);
      const errorMsg =
        error.response?.data?.message || "Error saving avatar configuration.";
      if (isSetupMode) onError(errorMsg);
      else setError(errorMsg);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    triggerSave
  }));

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  /* -------------------------------------------------------------
   * 7.  Styles helpers
   * -------------------------------------------------------------*/
  const mainContentStyle = isSetupMode
    ? { padding: "0", margin: "0" }
    : { padding: "0" };
  const cardBodyStyle = isSetupMode
    ? { paddingTop: "1rem", paddingBottom: "1rem" }
    : {};

  if (!token && loading) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography color="error">
          Authentication token is missing. Cannot load avatar settings.
        </Typography>
      </Box>
    );
  }

  /* -------------------------------------------------------------
   * 8.  Render
   * -------------------------------------------------------------*/
  return (
    <div style={mainContentStyle}>
      {!isSetupMode && (
        <div className="container-fluid p-0">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">
              <strong>Avatar</strong> Setting
            </h1>
          </div>
        </div>
      )}

      <div className={!isSetupMode ? "row" : ""}>
        <div className={!isSetupMode ? "col-12 col-lg-12" : ""}>
          <div className={!isSetupMode ? "card" : ""}>
            <div
              className={!isSetupMode ? "card-body" : ""}
              style={cardBodyStyle}
            >
              <h5
                className="card-title mb-0"
                style={
                  isSetupMode
                    ? { textAlign: "center", marginBottom: "1rem" }
                    : {}
                }
              >
                Choose your avatar
              </h5>
              {isSetupMode && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ textAlign: "center", mb: 2 }}
                >
                  Select an avatar that will represent your chatbot.
                </Typography>
              )}
              {!isSetupMode && <br />}

              {/* Filters and Search Bar */}
              <div className="row mb-3">
                <div className="col-md-4 mb-2">
                  <TextField
                    fullWidth
                    label="Search by Name, Type, Category"
                    variant="outlined"
                    size="small"
                    name="search"
                    value={filters.search}
                    onChange={handleFilterChange}
                  />
                </div>
                <div className="col-md-4 mb-2">
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      value={filters.type}
                      label="Type"
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="">
                        <em>All Types</em>
                      </MenuItem>
                      {filterOptions.types.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
                <div className="col-md-4 mb-2">
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={filters.category}
                      label="Category"
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="">
                        <em>All Categories</em>
                      </MenuItem>
                      {filterOptions.categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              </div>

              {/* Saved Avatar Row (only after save) */}
              {savedAvatar && (
                <>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                  >
                    Selected Avatar
                  </Typography>
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="col-6 col-md-3 mb-3">
                        <div
                        className="card avatar_selection selected"
                        style={{
                          cursor: "pointer"
                        }}
                      >
                        <div
                          className="avatar-icons"
                          style={{ opacity: 1 }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="feather feather-check-circle align-middle me-2"
                          >
                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                            <polyline points="22 4 12 14.01 9 11.01" />
                          </svg>
                        </div>
                        <img
                          className="card-img-top"
                          src={`/images/avatars/${savedAvatar.avatar_img_name}`}
                          alt={savedAvatar.avatar_name}
                        />
                        <div className="card-header">
                          <h5 className="card-title mb-0">
                            {savedAvatar.avatar_name}
                          </h5>
                        </div>
                      </div>
                        </div>
                      
                    </div>
                  </div>
                </>
              )}

              {/* Available Avatars Grid */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <Typography>Loading avatars...</Typography>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ mt: 2, mb: 1, fontWeight: 600 }}
                  >
                    Available Avatars
                  </Typography>
                  <div id="select_avatar" className="row">
                    {avatars.length > 0 ? (
                      avatars.map((avatar) => (
                        <div
                          className="col-6 col-md-3 mb-3"
                          key={avatar.id}
                          style={
                            isSetupMode
                              ? {
                                  display: "flex",
                                  justifyContent: "center"
                                }
                              : {}
                          }
                        >
                          <div
                            className={handleAvatarCardClassName(avatar)}
                            onClick={() => handleAvatarSelection(avatar)}
                            style={{ cursor: "pointer", width: "100%" }}
                          >
                            <div
                              className="avatar-icons"
                              style={{
                                opacity:
                                  selectedAvatar?.avatar_img_name ===
                                  avatar.avatar_img_name
                                    ? 1
                                    : 0
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="feather feather-check-circle align-middle me-2"
                              >
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                <polyline points="22 4 12 14.01 9 11.01" />
                              </svg>
                            </div>
                            <img
                              className="card-img-top"
                              src={`/images/avatars/${avatar.avatar_img_name}`}
                              alt={avatar.avatar_name}
                            />
                            <div
                              className="card-header"
                              style={isSetupMode ? { padding: "0.5rem" } : {}}
                            >
                              <h5
                                className="card-title mb-0"
                                style={isSetupMode ? { fontSize: "0.9rem" } : {}}
                              >
                                {avatar.avatar_name}
                              </h5>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <Box sx={{ textAlign: "center", width: "100%", p: 3 }}>
                        <Typography>
                          No avatars found matching your criteria.
                        </Typography>
                      </Box>
                    )}
                  </div>
                </>
              )}

              {pagination.total_pages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                  <Pagination
                    count={pagination.total_pages}
                    page={pagination.page}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        color: "white !important"
                      }
                    }}
                  />
                </Box>
              )}

              {!isSetupMode && (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3"
                    onClick={triggerSave}
                    disabled={
                      isSaving || !selectedAvatar || !selectedAvatar.id
                    }
                  >
                    {isSaving ? (
                      <>
                        <i className="fa fa-spinner fa-spin"></i> Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-save"></i> Save
                      </>
                    )}
                  </button>
                  <br />
                  {errorDiv && (
                    <div
                      id="getError"
                      className={`alert alert-warning mt-2 ${
                        errorDiv ? "" : "d-none"
                      }`}
                    >
                      {errorDiv}
                    </div>
                  )}
                </>
              )}
              {isSetupMode && errorDiv && (
                <Typography color="error" sx={{ mt: 1, textAlign: "center" }}>
                  {errorDiv}
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AvatarSettings;